import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { DrivesState, Drive } from "./types";
import { DriveType } from "./types";

const initialState: DrivesState = {
  shipSize: 100,
  techLevel: 12,
  drives: [],
  breakawayHull: {
    isBreakaway: false,
    sections: [],
    combinedPerformance: {
      maneuver: -1,
      jump: -1,
      reaction: -1,
      totalPower: 0,
      totalCost: 0,
      totalWeight: 0,
    },
  },
  isLoading: false,
  error: null,
};

const drivesSlice = createSlice({
  name: "drives",
  initialState,
  reducers: {
    // Ship size and tech level
    setShipSize: (state, action: PayloadAction<number>) => {
      state.shipSize = action.payload;
      state.error = null;
    },
    
    setTechLevel: (state, action: PayloadAction<number>) => {
      state.techLevel = action.payload;
      state.error = null;
    },

    // Individual drive actions
    setManeuverDrive: (state, action: PayloadAction<number | "none">) => {
      const rating = action.payload;
      const existingIndex = state.drives.findIndex(d => d.type === DriveType.MANEUVER);
      
      if (rating === "none") {
        if (existingIndex !== -1) {
          state.drives.splice(existingIndex, 1);
        }
      } else {
        const drive: Drive = {
          type: DriveType.MANEUVER,
          rating,
          techLevel: state.techLevel,
          description: `Maneuver Drive Rating ${rating}`,
        };
        
        if (existingIndex !== -1) {
          state.drives[existingIndex] = drive;
        } else {
          state.drives.push(drive);
        }
      }
      state.error = null;
    },

    setJumpDrive: (state, action: PayloadAction<number | "none">) => {
      const rating = action.payload;
      const existingIndex = state.drives.findIndex(d => d.type === DriveType.JUMP);
      
      if (rating === "none") {
        if (existingIndex !== -1) {
          state.drives.splice(existingIndex, 1);
        }
      } else {
        const drive: Drive = {
          type: DriveType.JUMP,
          rating,
          techLevel: state.techLevel,
          description: `Jump Drive Rating ${rating}`,
        };
        
        if (existingIndex !== -1) {
          state.drives[existingIndex] = drive;
        } else {
          state.drives.push(drive);
        }
      }
      state.error = null;
    },

    setReactionDrive: (state, action: PayloadAction<number | "none">) => {
      const rating = action.payload;
      const existingIndex = state.drives.findIndex(d => d.type === DriveType.REACTION);
      
      if (rating === "none") {
        if (existingIndex !== -1) {
          state.drives.splice(existingIndex, 1);
        }
      } else {
        const drive: Drive = {
          type: DriveType.REACTION,
          rating,
          techLevel: state.techLevel,
          description: `Reaction Drive Rating ${rating}`,
        };
        
        if (existingIndex !== -1) {
          state.drives[existingIndex] = drive;
        } else {
          state.drives.push(drive);
        }
      }
      state.error = null;
    },

    // Generic drive setter
    setDrive: (state, action: PayloadAction<{ type: DriveType; rating: number | "none" }>) => {
      const { type, rating } = action.payload;
      const existingIndex = state.drives.findIndex(d => d.type === type);
      
      if (rating === "none") {
        if (existingIndex !== -1) {
          state.drives.splice(existingIndex, 1);
        }
      } else {
        const drive: Drive = {
          type,
          rating,
          techLevel: state.techLevel,
          description: `${type.charAt(0).toUpperCase() + type.slice(1)} Drive Rating ${rating}`,
        };
        
        if (existingIndex !== -1) {
          state.drives[existingIndex] = drive;
        } else {
          state.drives.push(drive);
        }
      }
      state.error = null;
    },

    // Set all drives at once
    setDrives: (state, action: PayloadAction<Drive[]>) => {
      state.drives = action.payload;
      state.error = null;
    },

    // Clear all drives
    clearDrives: (state) => {
      state.drives = [];
      state.error = null;
    },

    // Breakaway hull actions
    setBreakawayHull: (state, action: PayloadAction<boolean>) => {
      state.breakawayHull = {
        ...state.breakawayHull,
        isBreakaway: action.payload,
        sections: state.breakawayHull?.sections || [],
        combinedPerformance: state.breakawayHull?.combinedPerformance || {},
      };
      state.error = null;
    },

    setBreakawaySections: (state, action: PayloadAction<any[]>) => {
      state.breakawayHull = {
        ...state.breakawayHull,
        isBreakaway: state.breakawayHull?.isBreakaway || false,
        sections: action.payload,
        combinedPerformance: state.breakawayHull?.combinedPerformance || {},
      };
      state.error = null;
    },

    updateBreakawaySection: (state, action: PayloadAction<{ sectionId: string; updates: any }>) => {
      const { sectionId, updates } = action.payload;
      if (!state.breakawayHull?.sections) return;
      
      const sectionIndex = state.breakawayHull.sections.findIndex(s => s.id === sectionId);
      
      if (sectionIndex !== -1) {
        state.breakawayHull.sections[sectionIndex] = {
          ...state.breakawayHull.sections[sectionIndex],
          ...updates,
        };
      }
      state.error = null;
    },

    updateBreakawayDrives: (state, action: PayloadAction<{ sectionId: string; driveType: DriveType; rating: number | "none" }>) => {
      const { sectionId, driveType, rating } = action.payload;
      if (!state.breakawayHull?.sections) return;
      
      const sectionIndex = state.breakawayHull.sections.findIndex(s => s.id === sectionId);
      
      if (sectionIndex !== -1) {
        const section = state.breakawayHull.sections[sectionIndex];
        if (!section.drives) {
          section.drives = { maneuver: null, jump: null, reaction: null };
        }
        
        if (rating === "none") {
          section.drives[driveType] = null;
        } else {
          section.drives[driveType] = {
            type: driveType,
            rating,
            techLevel: state.techLevel,
            description: `${driveType.charAt(0).toUpperCase() + driveType.slice(1)} Drive Rating ${rating}`,
            section: sectionIndex,
          };
        }
      }
      state.error = null;
    },

    updateCombinedPerformance: (state, action: PayloadAction<any>) => {
      state.breakawayHull = {
        ...state.breakawayHull,
        isBreakaway: state.breakawayHull?.isBreakaway || false,
        sections: state.breakawayHull?.sections || [],
        combinedPerformance: action.payload,
      };
      state.error = null;
    },

    // Loading and error states
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.isLoading = false;
    },

    // Reset to initial state
    reset: (state) => {
      return initialState;
    },
  },
});

export const {
  setShipSize,
  setTechLevel,
  setManeuverDrive,
  setJumpDrive,
  setReactionDrive,
  setDrive,
  setDrives,
  clearDrives,
  setBreakawayHull,
  setBreakawaySections,
  updateBreakawaySection,
  updateBreakawayDrives,
  updateCombinedPerformance,
  setLoading,
  setError,
  reset,
} = drivesSlice.actions;

export default drivesSlice.reducer;
